import type { Configuration, AccountInfo } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';
import type { AppRole } from '../types/auth';

/**
 * MSAL configuration for Entra ID authentication.
 *
 * Client ID and Tenant ID are read from environment variables.
 * See .env.example for required variables.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'ca3dfc8e-f323-409e-b6d6-211385417ef8'}`,
    // MSAL v5 redirect bridge (ISS-295): sign-ins return to a tiny page that hands the response to the waiting
    // window, never to the app itself. All three hosts' `/redirect.html` are registered on the Entra app (SPA).
    redirectUri: `${window.location.origin}/redirect.html`,
  },
  cache: {
    cacheLocation: 'localStorage', // SSO across tabs
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
        }
      },
    },
  },
};

/** Scopes requested during login — includes Graph API scopes so tokens
 *  are acquired upfront (avoids silent iframe renewal which times out
 *  in Edge due to cross-origin / third-party cookie restrictions) */
export const loginRequest = {
  scopes: ['User.Read', 'Sites.ReadWrite.All'],
};

/** Scopes for Microsoft Graph API access (SharePoint) */
export const graphScopes = {
  scopes: ['User.Read', 'Sites.ReadWrite.All'],
};

/**
 * Extract QMS App Roles from an MSAL account's ID token claims.
 *
 * Roles are the **Entra App Roles** assigned in the QMS Enterprise Application
 * (`idTokenClaims.roles`) — the dashboard's RBAC mechanism (04-CONTEXT.md §2),
 * NOT SharePoint security groups. The expected values are the `AppRole` union
 * (Oversight / CareManager / HRWHS / Finance + the field-reporter roles); see
 * 04-04-rbac-resolved.md §5 for the App Roles Andy provisions in Entra.
 *
 * Any value Entra returns is passed through (typed as `AppRole`); RoleGuard +
 * `OFFICE_ROLES` decide entry, so an unrecognized claim simply matches nothing.
 */
export function getRolesFromAccount(account: AccountInfo | null): AppRole[] {
  if (!account) return [];
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const roles = claims?.roles;
  if (Array.isArray(roles)) {
    return roles as AppRole[];
  }
  return [];
}
